/**
 * KeyAccessDialog — WhoHasAccess-style access manager for a keybook
 * key (entry) or vault (collection).
 *
 * Mirrors the file/folder WhoHasAccessDialog's mental model with a
 * view switch across the three principal families:
 *   - People & teams — in-tenant grants (team / member / role), live
 *     today via entry_access / collection_access. Rows carry a
 *     permission select (the POST /access endpoint is an upsert, so
 *     changing a tier is one call) and a remove button.
 *   - PDApp & AI — how app/agent principals reach this key. Direct
 *     app grants aren't in the ACL tables yet (recipientType is
 *     team|email|role only), so this view is honest: it surfaces the
 *     Key ID (the external handle scripts/PDApps bind by) and
 *     explains the credential-request flow agents use, with the grant
 *     affordance visibly staged as coming soon.
 *   - Outsiders — cross-tenant sharing. Not available yet (no keybook
 *     route implements it; assertCrossTenantShareAllowed is the guard
 *     any future one must call). github/oauth types can NEVER cross
 *     tenants (token is bound to the authorizing account) — say so.
 *
 * e2e contract (e2e/keybook/share.spec.ts) — the selectors below are
 * pinned and must not change:
 *   heading "Manage access" · aria-label "Close" · share-tab-team|
 *   email|role · "Team/Member/Role to grant access to" select labels ·
 *   "Permission level" · share-add · share-row (+data-recipient-type) ·
 *   "Remove <label>" buttons · role rows labelled "Role: <Label>" ·
 *   share-admin-acting-callout
 */
import { KeyAppsList } from './KeyAppsList.js'
import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from '@papan/i18n'
import { useBrand } from '@papan/brand/react'
import { useTheme, getThemeClass, useToast, avatarBg, getInitials } from '@papan/core-ui/ui'
import { request } from '@papan/api-client'
import { AccessSettingsPopover } from '@papan/org-connections/ui'
import { erasedOwnerEmail, roleCanTarget, CAP_ROLES } from '@papan/types'
import {
  X, Plus, Users, AtSign, Tag, Loader2, KeyRound, Library,
  Box, Network, Copy, Check, Bot, ChevronDown, UsersRound, Shield, ArrowLeft,
} from 'lucide-react'
import { keybookApi, type GrantLevel, type GrantRow, type InheritedAccess } from '../client/api.js'

type RecipientType = 'team' | 'email' | 'role'
type ViewMode = 'people' | 'apps' | 'outsiders'

interface AccessRow {
  recipientType: RecipientType
  recipientId: string
  permission: GrantLevel
  grantedBy?: string | null
  grantedAt?: string
}

interface TeamOption { id: string; name: string; memberCount?: number }
interface MemberOption { email: string; role: string; name?: string }

/** The thing being shared — a key (entry) or a vault (collection). */
export type ShareSubject =
  | { kind: 'entry'; entryId: string; label: string; credentialType?: string }
  | { kind: 'collection'; collectionId: string; label: string }
  /** The keybook ROOT — grants above every collection and key (the root access
   *  dialog, for whoever manages the root). */
  | { kind: 'root'; label: string }

interface KeyAccessDialogProps {
  subject: ShareSubject
  tenantId: string
  /** Caller's active org email — filtered out of the Member picker. */
  callerEmail?: string
  /** Caller's tenant role — only the owner or an admin may set the Admin role's
   *  access (the role-rank rule; the server re-checks). */
  callerRole?: string
  /** View-only (D27): the access list as Write — or the manage_keybook_permission
   *  capability — sees it. No Add, no level changes, no removing; "No access" rows
   *  are hidden by the server; people view only. */
  readOnly?: boolean
  /** A personal keybook is shared with people only — no Role or Team tab (the
   *  server refuses team and role rows there too). Default: an organization. */
  isOrg?: boolean
  onClose: () => void
}

/** Display name derived from an email's local part ("jane.doe" →
 *  "Jane Doe") — same derivation the WhoHasAccess cards use. */
function nameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? email
  return local.split(/[._-]+/).filter(Boolean)
    .map(s => s[0].toUpperCase() + s.slice(1)).join(' ') || email
}

/** Roles a grant row may name, by their official names (`CAP_ROLES` — the
 *  drive's and Settings' "Drive admin", "Member"…). The Drive admin role is
 *  offered only to the owner or a drive admin (the role-rank rule). */
const SHAREABLE_ROLES: { value: string; label: string }[] = (['admin', 'member', 'viewer', 'guest'] as const)
  .map(id => ({ value: id, label: CAP_ROLES.find(r => r.id === id)!.label }))

export function KeyAccessDialog({ subject, tenantId, callerEmail, callerRole, readOnly = false, isOrg = true, onClose }: KeyAccessDialogProps) {
  const { t } = useTranslation('keybook')
  const { productName } = useBrand()
  const isCollection = subject.kind === 'collection'
  const isRoot = subject.kind === 'root'
  const subjectId = subject.kind === 'collection' ? subject.collectionId : subject.kind === 'entry' ? subject.entryId : tenantId
  const credentialType = subject.kind === 'entry' ? subject.credentialType : undefined
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const toast = useToast()

  const [view, setView] = useState<ViewMode>('people')
  /** Granting is a DELIBERATE two-page popover anchored off "+ Add":
   *  page 'who' (recipient tabs + a list of avatar rows) → page 'perm'
   *  (permission choice for the picked recipient). Portaled to <body>,
   *  positioned off the button rect (same idiom as the row ⋯ menu). */
  const [addOpen, setAddOpen] = useState(false)
  const [addPage, setAddPage] = useState<'who' | 'perm'>('who')
  const [addPos, setAddPos] = useState<{ top: number; left: number; anchorTop: number; flipped: boolean; room: number } | null>(null)
  const addMenuRef = useRef<HTMLDivElement | null>(null)
  /** "+ Add" — focus goes back to it when Escape closes the popover (its filter
   *  took focus on open; a keyboard user must not be dropped to the page). */
  const addTriggerRef = useRef<HTMLButtonElement | null>(null)
  /** The add popover's filter — the drive's Manage Access picker has one too. */
  const [recipientQuery, setRecipientQuery] = useState('')
  const closeAdd = useCallback(() => {
    setAddOpen(false)
    setAddPage('who')
    setPickedRecipient('')
    setPickedPermission('read')
    setRecipientQuery('')
  }, [])
  useEffect(() => {
    if (!addOpen) return
    const onDown = (e: MouseEvent) => {
      if (!addMenuRef.current?.contains(e.target as Node)) closeAdd()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { closeAdd(); addTriggerRef.current?.focus() } }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [addOpen, closeAdd])
  /** Per-row ⋯ menu (WhoHasAccess card-actions pattern): permission
   *  options + Remove in one popover, portaled to <body>. */
  const [rowMenu, setRowMenu] = useState<{ row: AccessRow; pos: { top: number; left: number }; anchorTop: number; inherited?: boolean; isDefault?: boolean } | null>(null)
  const rowMenuRef = useRef<HTMLDivElement | null>(null)

  // Both popovers are fixed, positioned off their trigger's rect. The row menu
  // (fixed content) is measured before paint and flips above its trigger when
  // it would run past the bottom of the viewport; the Add popover picks its
  // side at open (see "+ Add").
  // A scroll or resize anywhere but inside the popover closes it — the trigger
  // lives in the dialog's scroller, so a fixed popover would detach from it.
  const flipAbove = (el: Element | null | undefined, top: number, anchorTop: number, gap: number): number | null => {
    const h = (el as HTMLElement | null)?.offsetHeight ?? 0
    if (!h || top + h <= window.innerHeight - 8) return null
    const above = Math.max(8, anchorTop - gap - h)
    return above === top ? null : above
  }
  useLayoutEffect(() => {
    if (!rowMenu) return
    const next = flipAbove(rowMenuRef.current?.firstElementChild, rowMenu.pos.top, rowMenu.anchorTop, 4)
    if (next !== null) setRowMenu(m => m && { ...m, pos: { ...m.pos, top: next } })
  }, [rowMenu])
  useEffect(() => {
    if (!addOpen && !rowMenu) return
    const onMove = (e: Event) => {
      const t = e.target as Node | null
      if (t && (addMenuRef.current?.contains(t) || rowMenuRef.current?.contains(t))) return
      setRowMenu(null)
      if (addOpen) closeAdd()
    }
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [addOpen, rowMenu, closeAdd])
  useEffect(() => {
    if (!rowMenu) return
    const onDown = (e: MouseEvent) => {
      if (!rowMenuRef.current?.contains(e.target as Node)) setRowMenu(null)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setRowMenu(null) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [rowMenu])
  const [accessRows, setAccessRows] = useState<AccessRow[]>([])
  const [teams, setTeams] = useState<TeamOption[]>([])
  const [members, setMembers] = useState<MemberOption[]>([])
  const [entryOwner, setEntryOwner] = useState<{ type: 'email' | 'team' | 'role'; id: string } | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [copiedId, setCopiedId] = useState(false)

  const [tab, setTab] = useState<RecipientType>(isOrg ? 'role' : 'email')
  const [pickedRecipient, setPickedRecipient] = useState<string>('')
  const [pickedPermission, setPickedPermission] = useState<GrantLevel>('read')
  /** What reaches this item from above (key / collection). */
  const [inherited, setInherited] = useState<InheritedAccess | null>(null)
  /** The root dialog's extras. */
  const [rootInfo, setRootInfo] = useState<{ adminDefault: GrantLevel; adminManageBelow: Array<{ level: 'collection' | 'key'; id: string; name: string }> } | null>(null)

  // Highest first, as the drive's levels: Manage, Read & Write, Read-only, No access.
  const PERMISSIONS: { value: GrantLevel; label: string; hint: string }[] = [
    { value: 'manage', label: t('access.permManage'), hint: t('access.permManageHint') },
    { value: 'write', label: t('access.permWrite'), hint: t('access.permWriteHint') },
    { value: 'read', label: t('access.permRead'), hint: t('access.permReadHint') },
    { value: 'none', label: t('access.permNone'), hint: t('access.permNoneHint') },
  ]

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      // Hit the orgs endpoints directly via api-client to avoid an
      // @papan/orgs feature dep loop (orgs already depends on keybook).
      const teamsP = request<{ teams: Array<{ id: string; name: string; memberCount?: number }> }>(
        `/api/orgs/${tenantId}/teams`,
      ).then(r => r.teams).catch(() => [])
      const membersP = request<{ members: Array<{ email: string; role: string; name?: string }> }>(
        `/api/orgs/${tenantId}/members`,
      ).then(r => r.members ?? []).catch(() => [])

      if (isRoot) {
        const [root, teamList, memberList] = await Promise.all([
          keybookApi.getRootAccess(tenantId), teamsP, membersP,
        ])
        setAccessRows(root.access)
        setRootInfo({ adminDefault: root.adminDefault, adminManageBelow: root.adminManageBelow })
        setInherited(null)
        setEntryOwner(null)
        setTeams(teamList)
        setMembers(memberList)
      } else if (isCollection) {
        const [res, teamList, memberList] = await Promise.all([
          keybookApi.getCollectionAccess(subjectId), teamsP, membersP,
        ])
        setAccessRows(res.access)
        setInherited(res.inherited ?? null)
        setEntryOwner(null) // collections have no per-key owner
        setTeams(teamList)
        setMembers(memberList)
      } else {
        // The key's access list WITHOUT its value (GET /entries/:id/access) — the
        // full answer for Manage, the read-only one otherwise. Never GET /entries/:id:
        // that decrypts the value and records the opener as having OPENED the key.
        const [res, teamList, memberList] = await Promise.all([
          keybookApi.getEntryAccess(subjectId), teamsP, membersP,
        ])
        setAccessRows(readOnly ? res.accessRows.filter(r => r.permission !== 'none') : res.accessRows)
        setInherited(res.inherited ?? null)
        setEntryOwner({ type: res.owner.type, id: res.owner.id })
        setTeams(teamList)
        setMembers(memberList)
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load access info')
    } finally {
      setLoading(false)
    }
  }, [subjectId, isCollection, isRoot, readOnly, tenantId, toast])

  useEffect(() => { void reload() }, [reload])

  const usedKeys = new Set(accessRows.map(r => `${r.recipientType}:${r.recipientId}`))
  const availableTeams = teams.filter(tm => !usedKeys.has(`team:${tm.id}`))
  const callerEmailLower = callerEmail?.toLowerCase()
  // The key's email owner manages it by ownership — no grant row is written for
  // them any more, so the roster shows them as a FIXED row (transfer ownership to
  // change it). A deleted account's 'deleted:' owner is a record, not a person.
  const ownerEmail = entryOwner?.type === 'email' && !erasedOwnerEmail(entryOwner.id) ? entryOwner.id.toLowerCase() : null
  const showFixedOwner = !!ownerEmail && !accessRows.some(r => r.recipientType === 'email' && r.recipientId.toLowerCase() === ownerEmail)
  // Not offered as recipients: the caller, the key's owner and the org's owner
  // (who always manages every key). Admins stay offered — whether a grant to
  // one does anything depends on the org's capability settings, which only the
  // server knows; it answers a grant to someone who already manages every key
  // as a no-op, and the dialog says so.
  const availableMembers = members.filter(m =>
    !usedKeys.has(`email:${m.email.toLowerCase()}`)
    && m.email.toLowerCase() !== callerEmailLower
    && m.email.toLowerCase() !== ownerEmail
    && m.role !== 'owner',
  )
  // The role-rank rule (as folders): no row for a role above your own.
  // On the keybook-wide list Drive admin is always its own row (the default, or
  // the level set for it) — changed there, never added.
  const availableRoles = SHAREABLE_ROLES.filter(r => !usedKeys.has(`role:${r.value}`) && roleCanTarget(callerRole, r.value) && !(isRoot && r.value === 'admin'))

  const labelForRow = useCallback((row: AccessRow): string => {
    if (row.recipientType === 'team') return teams.find(tm => tm.id === row.recipientId)?.name ?? row.recipientId
    if (row.recipientType === 'email') return row.recipientId
    const r = SHAREABLE_ROLES.find(sr => sr.value === row.recipientId)
    return r ? `Role: ${r.label}` : `Role: ${row.recipientId}`
  }, [teams])

  /** Identity of a grant row: emails match case-insensitively (stored lowercased
   *  by the server), team and role ids exactly — team ids are mixed case. */
  const grantKey = (r: { recipientType: string; recipientId: string }) =>
    `${r.recipientType}:${r.recipientType === 'email' ? r.recipientId.toLowerCase() : r.recipientId}`
  /** What reaches this item from ABOVE, by principal — the closest level wins
   *  (a collection's row over a keybook-wide one). These are GRANTS per
   *  principal, as the drive's Who has access lists them — not each person's
   *  resolved access: a person can hold a grant from above and still resolve
   *  differently through another principal's row (a role or team at a closer
   *  level). Who can actually open the key is the Exposure view. Shown in the main list as the
   *  drive's Who has access shows inherited grants: an "Inherited" row whose menu
   *  offers "Inherit (level)" and the levels; picking a level sets it HERE. A
   *  level the viewer may not see (rows null) is only named, below the list. */
  const inheritedByKey = useMemo(() => {
    // `uncertain`: a CLOSER level is hidden from this viewer (its rows null) and
    // may override this one — so this level is shown, never claimed as the one
    // that applies.
    const m = new Map<string, { row: AccessRow; where: string; uncertain: boolean }>()
    if (!inherited) return m
    const collectionHidden = !!inherited.collection && inherited.collection.rows === null
    const levels = [
      ...(inherited.collection?.rows ? [{ where: t('access.inheritedFromCollection', { name: inherited.collection.name }), rows: inherited.collection.rows, uncertain: false }] : []),
      ...(inherited.root.rows ? [{ where: t('access.inheritedKeybookWide'), rows: inherited.root.rows, uncertain: collectionHidden }] : []),
    ]
    for (const l of levels) for (const r of l.rows) {
      // The row keeps its own id: the server looks team ids up exactly.
      const k = grantKey(r)
      if (!m.has(k)) m.set(k, { row: { recipientType: r.recipientType as RecipientType, recipientId: r.recipientId, permission: r.permission as GrantLevel }, where: l.where, uncertain: l.uncertain })
    }
    return m
  }, [inherited, t])
  /** Keybook-wide, the Drive admin role has a DEFAULT level (in code) that a row
   *  for it replaces — its menu offers "Default (level)" to go back, as an
   *  inherited row offers "Inherit". */
  const defaultFor = (row: { recipientType: string; recipientId: string }) =>
    isRoot && rootInfo && row.recipientType === 'role' && row.recipientId === 'admin' ? rootInfo.adminDefault : null
  const inheritedFor = (row: { recipientType: string; recipientId: string }) =>
    inheritedByKey.get(grantKey(row)) ?? null
  /** Inherited principals with no row of their own here — the explicit row, when
   *  there is one, replaces the inherited level (it shows "Inherit" in its menu). */
  const inheritedRows = useMemo(() => {
    const explicit = new Set(accessRows.map(grantKey))
    const out: Array<{ row: AccessRow; where: string; uncertain: boolean }> = []
    for (const [k, v] of inheritedByKey) {
      if (explicit.has(k)) continue
      // The key's owner is the fixed Owner row (the owner floor: Manage) — a row
      // from above for them changes nothing, and is not offered to change.
      if (v.row.recipientType === 'email' && ownerEmail && v.row.recipientId.toLowerCase() === ownerEmail) continue
      // Read-only lists hide "No access" rows, inherited ones too.
      if (readOnly && v.row.permission === 'none') continue
      out.push({ row: v.row, where: v.where, uncertain: v.uncertain })
    }
    return out
  }, [inheritedByKey, accessRows, readOnly, ownerEmail])
  /** Levels above whose rows the viewer may not see: named only. */
  const hiddenInheritedLevels = !inherited ? [] : [
    ...(inherited.collection && inherited.collection.rows === null ? [t('access.inheritedFromHiddenCollection')] : []),
    ...(inherited.root.rows === null ? [t('access.inheritedKeybookWide')] : []),
  ]

  const upsertAccess = useCallback(async (payload: GrantRow) => {
    return isRoot
      ? keybookApi.addRootAccess(tenantId, payload)
      : isCollection
        ? keybookApi.addCollectionAccess(subjectId, payload)
        : keybookApi.addEntryAccess(subjectId, payload)
  }, [isRoot, isCollection, subjectId, tenantId])

  const handleAdd = async () => {
    if (!pickedRecipient || busy) return
    setBusy(true)
    try {
      await upsertAccess({ recipientType: tab, recipientId: pickedRecipient, permission: pickedPermission })
      toast.success(t('access.granted'))
      closeAdd()
      addTriggerRef.current?.focus() // the popover's Add button is gone: back to "+ Add"
      await reload()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to grant access')
    } finally {
      setBusy(false)
    }
  }

  /** The POST /access endpoint upserts on the (recipientType,
   *  recipientId) PK, so changing a tier is the same call as granting. */
  const handleChangePermission = async (row: AccessRow, permission: GrantLevel, makeExplicit = false) => {
    // `makeExplicit`: an inherited row — any level picked writes a row HERE, the
    // inherited one included (it then stays when the level above changes).
    if (busy || (!makeExplicit && permission === row.permission)) return
    setBusy(true)
    try {
      await upsertAccess({ recipientType: row.recipientType, recipientId: row.recipientId, permission })
      toast.success(t('access.updated'))
      await reload()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update access')
      await reload() // put the select back to server truth
    } finally {
      setBusy(false)
    }
  }

  const handleRevoke = async (row: AccessRow) => {
    if (busy) return
    setBusy(true)
    try {
      if (isRoot) await keybookApi.revokeRootAccess(tenantId, row.recipientType, row.recipientId)
      else if (isCollection) await keybookApi.revokeCollectionAccess(subjectId, row.recipientType, row.recipientId)
      else await keybookApi.revokeEntryAccess(subjectId, row.recipientType, row.recipientId)
      toast.success(t('access.revoked'))
      await reload()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to revoke')
    } finally {
      setBusy(false)
    }
  }

  const handleCopyId = async () => {
    if (subject.kind !== 'entry') return
    try {
      await navigator.clipboard.writeText(subject.entryId)
      setCopiedId(true)
      toast.success(t('actions.copied'))
      setTimeout(() => setCopiedId(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  /** Advance to the permission page for a chosen recipient. */
  const pickRecipient = (id: string) => {
    setPickedRecipient(id)
    setAddPage('perm')
  }

  /** One clickable recipient row (avatar + name), styled like the
   *  roster cards so page 1 reads as "pick from this list". */
  const RecipientRow = ({ avatar, name, sub, onClick }: {
    avatar: ReactNode; name: string; sub: string; onClick: () => void
  }) => (
    <button
      type="button"
      data-testid="add-recipient"
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors text-left cursor-pointer"
    >
      {avatar}
      <span className="flex-1 min-w-0">
        <span className="block text-sm themed-text truncate">{name}</span>
        <span className="block text-[11px] themed-text-muted truncate">{sub}</span>
      </span>
    </button>
  )

  /** Page 1 list of available recipients for the active tab. */
  const renderRecipientList = () => {
    // The filter matches what each row shows: a team's name, a person's name
    // or email, a role's name. Empty = everything.
    const q = recipientQuery.trim().toLowerCase()
    const hit = (...texts: Array<string | null | undefined>) => !q || texts.some(x => !!x && x.toLowerCase().includes(q))
    const noMatch = <p className="px-2 py-3 text-xs themed-text-muted" data-testid="add-recipient-no-match">{t('access.noMatch')}</p>
    if (tab === 'team') {
      if (teams.length === 0) return <p className="px-2 py-3 text-xs themed-text-muted">{t('access.noTeams')}</p>
      if (availableTeams.length === 0) return <p className="px-2 py-3 text-xs themed-text-muted">{t('access.allTeamsAdded')}</p>
      const shown = availableTeams.filter(tm => hit(tm.name))
      if (shown.length === 0) return noMatch
      return shown.map(tm => (
        <RecipientRow
          key={tm.id}
          onClick={() => pickRecipient(tm.id)}
          avatar={<span className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center shrink-0" aria-hidden><UsersRound size={13} className="text-white" /></span>}
          name={tm.name}
          sub={tm.memberCount != null ? t('access.teamMembers', { count: tm.memberCount }) : t('access.teamSub')}
        />
      ))
    }
    if (tab === 'email') {
      if (availableMembers.length === 0) return <p className="px-2 py-3 text-xs themed-text-muted">{t('access.allMembersAdded')}</p>
      const shown = availableMembers.filter(m => hit(m.name, nameFromEmail(m.email), m.email))
      if (shown.length === 0) return noMatch
      return shown.map(m => (
        <RecipientRow
          key={m.email}
          onClick={() => pickRecipient(m.email)}
          avatar={<span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shrink-0 select-none ${avatarBg(m.email)}`} aria-hidden>{getInitials(m.email)}</span>}
          name={m.name || nameFromEmail(m.email)}
          sub={m.email}
        />
      ))
    }
    if (availableRoles.length === 0) return <p className="px-2 py-3 text-xs themed-text-muted">{t('access.allRolesAdded')}</p>
    const shown = availableRoles.filter(r => hit(r.label))
    if (shown.length === 0) return noMatch
    return shown.map(r => (
      <RecipientRow
        key={r.value}
        onClick={() => pickRecipient(r.value)}
        avatar={<span className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center shrink-0" aria-hidden><Shield size={13} className="text-white" /></span>}
        name={r.label}
        sub={t('access.roleSub')}
      />
    ))
  }

  /** The picked recipient's identity, for the permission page header. */
  const pickedRecipientView = (): { avatar: ReactNode; name: string; sub: string } | null => {
    if (!pickedRecipient) return null
    if (tab === 'team') {
      const tm = teams.find(t => t.id === pickedRecipient)
      return {
        avatar: <span className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center shrink-0" aria-hidden><UsersRound size={13} className="text-white" /></span>,
        name: tm?.name ?? pickedRecipient,
        sub: tm?.memberCount != null ? t('access.teamMembers', { count: tm.memberCount }) : t('access.teamSub'),
      }
    }
    if (tab === 'email') {
      const m = members.find(m => m.email.toLowerCase() === pickedRecipient.toLowerCase())
      return {
        avatar: <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shrink-0 select-none ${avatarBg(pickedRecipient)}`} aria-hidden>{getInitials(pickedRecipient)}</span>,
        name: m?.name || nameFromEmail(pickedRecipient),
        sub: pickedRecipient,
      }
    }
    const r = SHAREABLE_ROLES.find(sr => sr.value === pickedRecipient)
    return {
      avatar: <span className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center shrink-0" aria-hidden><Shield size={13} className="text-white" /></span>,
      name: r?.label ?? pickedRecipient,
      sub: t('access.roleSub'),
    }
  }

  /** One row of the access list — an explicit row, or (with `inheritedWhere`) one
   *  that reaches this item from above. */
  const renderAccessRow = (row: AccessRow, inheritedWhere?: string, uncertain = false) => {
                      const label = labelForRow(row)
                      const isOwnerRow = !!entryOwner
                        && row.recipientType === entryOwner.type
                        && row.recipientId === entryOwner.id
                      // WhoHasAccess card identity: avatar + display name
                      // on top, the address/kind beneath.
                      const member = row.recipientType === 'email'
                        ? members.find(m => m.email.toLowerCase() === row.recipientId.toLowerCase())
                        : undefined
                      // Main text = the specific recipient; subtitle = its
                      // KIND (email / "Team · N" / "Role"). The avatar +
                      // subtitle carry the type, so the role row drops the
                      // "Role:" prefix from its main label.
                      const displayName = row.recipientType === 'email'
                        ? (member?.name || nameFromEmail(row.recipientId))
                        : row.recipientType === 'role'
                          ? (SHAREABLE_ROLES.find(sr => sr.value === row.recipientId)?.label ?? row.recipientId)
                          : label
                      const kind = row.recipientType === 'email'
                        ? row.recipientId
                        : row.recipientType === 'team'
                          ? (() => {
                              const count = teams.find(tm => tm.id === row.recipientId)?.memberCount
                              return count != null ? t('access.teamMembers', { count }) : t('access.teamSub')
                            })()
                          : t('access.roleSub')
                      // An inherited row says where it comes from.
                      const subtitle = inheritedWhere ? `${kind} · ${inheritedWhere}` : kind
                      const inh = inheritedWhere ? null : inheritedFor(row)
                      const inhLevel = inh && !inh.uncertain ? (PERMISSIONS.find(p => p.value === inh.row.permission)?.label ?? inh.row.permission) : null
                      return (
                        <li
                          key={`${inheritedWhere ? 'inherited' : 'row'}:${row.recipientType}:${row.recipientId}`}
                          className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-white/5 transition-colors"
                          data-testid={inheritedWhere ? 'share-row-inherited' : 'share-row'}
                          data-recipient-type={row.recipientType}
                        >
                          {/* WhoHasAccess CardAvatar scheme: member =
                              colored-initial circle, team = solid blue
                              UsersRound, role = solid amber Shield. */}
                          {row.recipientType === 'email' ? (
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shrink-0 select-none ${avatarBg(row.recipientId)}`} aria-hidden>
                              {getInitials(row.recipientId)}
                            </span>
                          ) : row.recipientType === 'team' ? (
                            <span className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center shrink-0" aria-hidden>
                              <UsersRound size={13} className="text-white" />
                            </span>
                          ) : (
                            <span className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center shrink-0" aria-hidden>
                              <Shield size={13} className="text-white" />
                            </span>
                          )}
                          <span className="flex-1 min-w-0">
                            <span className="text-sm themed-text truncate flex items-center gap-1.5">
                              <span className="truncate">{displayName}</span>
                              {inheritedWhere && (
                                <span className="kbg-pill text-[9px] uppercase tracking-wider px-1.5 py-0.5 shrink-0" title={uncertain ? t('access.inheritedUncertainTitle', { where: inheritedWhere }) : t('access.inheritedChipTitle', { where: inheritedWhere })}>{t('access.inheritedChip')}</span>
                              )}
                              {isOwnerRow && (
                                <span className="ws-chip-mine shrink-0" title={t('access.ownerChipTitle')}>{t('access.ownerChip')}</span>
                              )}
                            </span>
                            <span className="block text-[11px] themed-text-muted truncate">{subtitle}</span>
                          </span>
                          {/* The permission itself is the trigger — the
                              RecipientAccessPicker idiom: blue label +
                              chevron, opening the levels + Remove popover.
                              Read-only: the level as plain text. */}
                          {/* Read-only — or a level from above that a hidden, closer level
                              may override: shown as text, not offered to change. */}
                          {readOnly || uncertain ? (
                            <span className="shrink-0 px-1.5 py-1 text-sm themed-text-muted" data-testid="share-row-level">
                              {PERMISSIONS.find(p => p.value === row.permission)?.label ?? row.permission}
                            </span>
                          ) : <button
                            data-testid="share-row-menu"
                            aria-label={t('access.editFor', { name: label })}
                            aria-haspopup="menu"
                            aria-expanded={rowMenu?.row === row || undefined}
                            disabled={busy}
                            onClick={e => {
                              const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
                              setRowMenu({ row, pos: { top: r.bottom + 4, left: Math.max(8, r.right - 168) }, anchorTop: r.top, inherited: !!inheritedWhere })
                            }}
                            className="shrink-0 flex items-center gap-1 px-1.5 py-1 rounded-md hover:bg-white/10 disabled:opacity-50 cursor-pointer transition-colors"
                          >
                            <span className={`text-sm ${inheritedWhere ? 'themed-text-secondary' : 'text-blue-400'}`} title={inhLevel && inh ? t('access.overridesInherited', { level: inhLevel, where: inh.where }) : undefined}>
                              {PERMISSIONS.find(p => p.value === row.permission)?.label ?? row.permission}
                            </span>
                            <ChevronDown size={13} className="themed-text-muted" />
                          </button>}
                        </li>
                      )
  }

  const crossTenantBlocked = credentialType === 'github' || credentialType === 'oauth'

  const RECIPIENT_TABS: { id: RecipientType; label: string; Icon: typeof Users }[] = [
    ...(isOrg ? [
      { id: 'role' as const, label: t('access.tabRole'), Icon: Tag },
      { id: 'team' as const, label: t('access.tabTeam'), Icon: Users },
    ] : []),
    { id: 'email', label: t('access.tabMember'), Icon: AtSign },
  ]

  const VIEWS: { id: ViewMode; label: string; Icon: typeof UsersRound }[] = [
    { id: 'people', label: t('access.viewPeople'), Icon: UsersRound },
    { id: 'apps', label: t('access.viewApps'), Icon: Box },
    { id: 'outsiders', label: t('access.viewOutsiders'), Icon: Network },
  ]

  // Portaled: in-tree fixed overlays underlap the taskbar (ancestor
  // stacking context) — see KeybookView.
  return createPortal(
    <div className="kb-deck-portal fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} ws-glass-dialog w-full max-w-lg p-6 relative z-10 max-h-[85vh] flex flex-col`}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 themed-text-muted hover:themed-text cursor-pointer"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <h2 className="text-lg font-semibold themed-text mb-1">{isRoot ? t('settings.title') : readOnly ? t('access.viewTitle') : 'Manage access'}</h2>
        {readOnly && <p className="text-[11px] themed-text-muted mb-2" data-testid="access-read-only-note">{t('access.readOnlyNote')}</p>}
        {isRoot ? (
          // Keybook settings: keybook-wide access is its first SECTION (room
          // for more sections later), not the dialog's whole identity.
          <div className="mt-2 mb-3" data-testid="settings-access-section">
            <div className="text-[10px] themed-text-muted uppercase tracking-wider mb-1">{t('settings.accessSection')}</div>
            <p className="text-xs themed-text-muted leading-snug">{t('settings.accessHint')}</p>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 mb-3 text-sm themed-text-muted min-w-0">
            {isCollection ? <Library className="w-3.5 h-3.5 shrink-0" /> : <KeyRound className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--kb-gold, #d4a843)' }} />}
            <span className="truncate">{subject.label}</span>
            <span className="kbg-pill text-[10px] px-1.5 py-0.5 shrink-0">
              {isCollection ? t('access.subjectCollection') : t('access.subjectKey')}
            </span>
          </div>
        )}

        {/* View switch — the WhoHasAccess principal families, a carved
            well with the active view carried by FILL. The root has people only. */}
        {!isRoot && !readOnly && <div className="kbg-well flex gap-1 mb-4 p-1" role="tablist" aria-label="Access view">
          {VIEWS.map(({ id, label, Icon }) => {
            const active = view === id
            return (
              <button
                key={id}
                role="tab"
                aria-selected={active}
                data-testid={`access-view-${id}`}
                onClick={() => setView(id)}
                className={`flex-1 px-2 py-1.5 rounded-lg text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer ${
                  active ? 'bg-[var(--ws-pill-active-bg)] themed-text font-medium' : 'themed-text-muted hover:themed-text'
                }`}
              >
                <Icon size={13} />
                {label}
              </button>
            )
          })}
        </div>}

        <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 space-y-4">
          {view === 'people' && (
            <>
              {/* Admin acting on someone else's key. */}
              {!readOnly && entryOwner
                && entryOwner.type === 'email'
                && callerEmailLower
                && entryOwner.id !== callerEmailLower && (
                <div
                  data-testid="share-admin-acting-callout"
                  className="rounded-[10px] bg-purple-500/15 p-3 text-xs themed-text leading-relaxed"
                >
                  You're managing access as an admin, not the owner. Changes
                  here apply to <strong>{erasedOwnerEmail(entryOwner.id) ? t('ownerDeletedAccount', { email: erasedOwnerEmail(entryOwner.id) }) : entryOwner.id}</strong>'s credential
                  and may surprise them.
                </div>
              )}

              {/* OAuth-flavoured creds carry the owner's identity. */}
              {credentialType === 'github' && entryOwner && (() => {
                const ownerIsCaller = entryOwner.type === 'email'
                  && !!callerEmailLower
                  && entryOwner.id === callerEmailLower
                return (
                  <div className="rounded-[10px] bg-amber-500/12 p-3 text-xs themed-text leading-relaxed">
                    {ownerIsCaller ? (
                      <>
                        ⚠ Sharing this credential lets others act as your
                        authorized GitHub account — their commits are attributed
                        to <strong>your</strong> GitHub identity, and revoking{' '}
                        {productName} on GitHub cuts everyone off. Consider asking
                        teammates to authorize their own install instead.
                      </>
                    ) : (
                      <>
                        ⚠ Recipients act as{' '}
                        <strong>{entryOwner.type === 'email' ? (erasedOwnerEmail(entryOwner.id) ? t('ownerDeletedAccount', { email: erasedOwnerEmail(entryOwner.id) }) : entryOwner.id) : 'the owner'}</strong>'s
                        authorized GitHub account — commits are attributed to the
                        owner's GitHub identity, not theirs.
                      </>
                    )}
                  </div>
                )
              })()}

              <section>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xs themed-text-muted uppercase tracking-wide">{t('access.hasAccess')}</h3>
                  {/* Adding someone is a DELIBERATE act — opens the
                      two-page add popover (who → permission). */}
                  {!readOnly && <button
                    ref={addTriggerRef}
                    type="button"
                    data-testid="share-open-add"
                    aria-haspopup="dialog"
                    aria-expanded={addOpen}
                    onClick={e => {
                      const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
                      setAddPage('who')
                      setTab(isOrg ? 'role' : 'email')
                      setPickedRecipient('')
                      setPickedPermission('read')
                      // Open on the side with more room, capped to it — a later height
                      // change (another tab, page 2) only scrolls the inner list.
                      const roomBelow = window.innerHeight - (r.bottom + 6) - 8
                      const roomAbove = r.top - 6 - 8
                      const flipped = roomBelow < 240 && roomAbove > roomBelow
                      setAddPos({
                        top: r.bottom + 6,
                        left: Math.max(8, Math.min(r.left, window.innerWidth - 312)),
                        anchorTop: r.top,
                        flipped,
                        room: Math.max(0, flipped ? roomAbove : roomBelow),
                      })
                      setAddOpen(true)
                    }}
                    className="ml-auto kbg-btn h-7 px-2.5 text-xs"
                  >
                    <Plus size={13} />
                    {t('access.add')}
                  </button>}
                </div>

                {/* The roster — single-line cards in ONE carved section;
                    each row's ⋯ opens permissions + remove. */}
                {loading ? (
                  <div className="flex items-center justify-center py-6 themed-text-muted">
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                ) : accessRows.length === 0 && !showFixedOwner && !isRoot ? (
                  <p className="text-sm themed-text-muted">{t('access.noOneYet')}</p>
                ) : (
                  <ul className="kbg-well p-1.5 space-y-0.5">
                    {/* The root: admins hold Manage by default (in code) until a
                        role:admin row says otherwise — only the owner lowers it. */}
                    {isRoot && rootInfo && !accessRows.some(r => r.recipientType === 'role' && r.recipientId === 'admin') && (
                      <li key="admin-default" className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg" data-testid="root-admin-default">
                        <span className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center shrink-0" aria-hidden>
                          <Shield size={13} className="text-white" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="text-sm themed-text truncate block">{SHAREABLE_ROLES.find(r => r.value === 'admin')!.label}</span>
                          <span className="block text-[11px] themed-text-muted truncate">{t('access.adminDefaultSub')}</span>
                        </span>
                        {/* The default is Manage, so any change LOWERS drive admins — the
                            owner's call (the server judges it too): the owner gets the
                            menu, everyone else the level as text. */}
                        {!readOnly && callerRole === 'owner' ? (
                          <button
                            data-testid="share-row-menu"
                            aria-label={t('access.editFor', { name: SHAREABLE_ROLES.find(r => r.value === 'admin')!.label })}
                            aria-haspopup="menu"
                            aria-expanded={rowMenu?.isDefault || undefined}
                            disabled={busy}
                            onClick={e => {
                              const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
                              setRowMenu({ row: { recipientType: 'role', recipientId: 'admin', permission: rootInfo.adminDefault }, pos: { top: r.bottom + 4, left: Math.max(8, r.right - 168) }, anchorTop: r.top, isDefault: true })
                            }}
                            className="shrink-0 flex items-center gap-1 px-1.5 py-1 rounded-md hover:bg-white/10 disabled:opacity-50 cursor-pointer transition-colors"
                          >
                            <span className="text-sm themed-text-secondary">{PERMISSIONS.find(p => p.value === rootInfo.adminDefault)?.label}</span>
                            <ChevronDown size={13} className="themed-text-muted" />
                          </button>
                        ) : (
                          <span className="shrink-0 px-1.5 py-1 text-sm themed-text-muted">
                            {PERMISSIONS.find(p => p.value === rootInfo.adminDefault)?.label}
                          </span>
                        )}
                      </li>
                    )}
                    {showFixedOwner && ownerEmail && (() => {
                      const m = members.find(mm => mm.email.toLowerCase() === ownerEmail)
                      return (
                        <li
                          key="owner"
                          className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg"
                          data-testid="share-row-owner"
                        >
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shrink-0 select-none ${avatarBg(ownerEmail)}`} aria-hidden>
                            {getInitials(ownerEmail)}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="text-sm themed-text truncate flex items-center gap-1.5">
                              <span className="truncate">{m?.name || nameFromEmail(ownerEmail)}</span>
                              <span className="ws-chip-mine shrink-0" title={t('access.ownerChipTitle')}>{t('access.ownerChip')}</span>
                            </span>
                            <span className="block text-[11px] themed-text-muted truncate">{ownerEmail}</span>
                          </span>
                          <span className="shrink-0 px-1.5 py-1 text-sm themed-text-muted" title={t('access.ownerFixedTitle')}>
                            {PERMISSIONS.find(p => p.value === 'manage')?.label}
                          </span>
                        </li>
                      )
                    })()}
                    {accessRows.map(row => renderAccessRow(row))}
                    {inheritedRows.map(({ row, where, uncertain }) => renderAccessRow(row, where, uncertain))}
                  </ul>
                )}
                {hiddenInheritedLevels.map(where => (
                  <p key={where} className="px-1 pt-1.5 text-[11px] themed-text-muted" data-testid="access-inherited-hidden">{t('access.inheritedHidden', { where })}</p>
                ))}
              </section>

              {/* The root: a team row here RAISES any admin in that team, whatever
                  the admins' row says (a team only raises an admin). */}
              {isRoot && callerRole === 'owner' && accessRows.some(r => r.recipientType === 'team') && (
                <p className="text-[11px] themed-text-muted" data-testid="root-team-hint">{t('access.rootTeamHint')}</p>
              )}

              {/* The root: where admins still hold Manage through a collection or
                  key row — a lowering here doesn't reach those. */}
              {isRoot && rootInfo && rootInfo.adminManageBelow.length > 0 && (
                <section data-testid="root-admin-below">
                  <h3 className="text-xs themed-text-muted uppercase tracking-wide mb-1">{t('access.adminBelowTitle')}</h3>
                  <p className="text-[11px] themed-text-muted mb-2">{t('access.adminBelowHint')}</p>
                  <ul className="kbg-well p-1.5 space-y-0.5">
                    {rootInfo.adminManageBelow.map(b => (
                      <li key={`${b.level}:${b.id}`} className="px-2.5 py-1.5 text-sm themed-text truncate">
                        {b.level === 'collection' ? <Library className="inline w-3.5 h-3.5 mr-1.5" /> : <KeyRound className="inline w-3.5 h-3.5 mr-1.5" />}
                        {b.name}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}

          {view === 'apps' && (
            <div className="space-y-3">
              {subject.kind === 'entry' && (
                <div className="kbg-well px-3 py-2.5 flex items-center gap-1.5 min-w-0">
                  <span className="text-[9px] themed-text-muted uppercase tracking-wider shrink-0">
                    {t('inspector.keyIdTitle')}
                  </span>
                  <code className="kbg-id min-w-0 flex-1 text-[11px] themed-text truncate select-all">
                    {subject.entryId}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopyId}
                    className="kbg-btn w-6 h-6 rounded-md shrink-0"
                    title={t('inspector.copyKeyId')}
                    aria-label={t('inspector.copyKeyId')}
                  >
                    {copiedId ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>
              )}
              <div className="kbg-well p-3 space-y-2">
                <div className="flex items-center gap-1.5 text-sm themed-text font-medium">
                  <Box className="w-4 h-4 themed-icon" />
                  {t('access.appsTitle')}
                </div>
                <p className="text-xs themed-text-muted leading-relaxed">
                  {subject.kind === 'entry' ? t('access.appsBody') : t('access.appsBodyCollection')}
                </p>
                {subject.kind === 'entry' && <KeyAppsList entryId={subject.entryId} />}
              </div>
              <div className="kbg-well p-3 space-y-2">
                <div className="flex items-center gap-1.5 text-sm themed-text font-medium">
                  <Bot className="w-4 h-4 themed-icon" />
                  {t('access.aiTitle')}
                </div>
                <p className="text-xs themed-text-muted leading-relaxed">
                  {t('access.aiBody')}
                </p>
              </div>
            </div>
          )}

          {view === 'outsiders' && (
            <div className="space-y-3">
              {crossTenantBlocked ? (
                <div className="rounded-[10px] bg-amber-500/12 p-3 text-xs themed-text leading-relaxed">
                  {t('access.outsiderBlockedOauth', { productName })}
                </div>
              ) : (
                <div className="kbg-well p-3 space-y-2">
                  <div className="flex items-center gap-1.5 text-sm themed-text font-medium">
                    <Network className="w-4 h-4 themed-icon" />
                    {t('access.outsidersTitle')}
                  </div>
                  <p className="text-xs themed-text-muted leading-relaxed">
                    {t('access.outsidersBody')}
                  </p>
                  <button
                    type="button"
                    disabled
                    title={t('access.comingSoon')}
                    className="kbg-btn px-3 py-1.5 text-xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {t('access.shareWithOutsider')}
                    <span className="kbg-pill text-[10px] px-1.5 py-0.5">{t('access.comingSoon')}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {rowMenu && createPortal(
        <div
          ref={rowMenuRef}
          className="kb-deck-portal"
          role="menu"
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        >
          <AccessSettingsPopover
            themeClass={themeClass}
            pos={rowMenu.pos}
            dropdownRef={rowMenuRef}
            permissionOptions={PERMISSIONS.map(p => p.value)}
            permissionLabels={Object.fromEntries(PERMISSIONS.map(p => [p.value, `${p.label}`]))}
            // An inherited row has no level of its own: "Inherit (…)" is the
            // checked choice, and any level picked is set HERE.
            selectedPermission={rowMenu.inherited || rowMenu.isDefault ? null : rowMenu.row.permission}
            onSelectPermission={opt => {
              const { row, inherited: fromAbove, isDefault } = rowMenu
              setRowMenu(null)
              void handleChangePermission(row, opt as GrantLevel, !!fromAbove || !!isDefault)
            }}
            inherit={(() => {
              // Keybook-wide Drive admin: "Default (level)" — checked on the default
              // row; on a set row it removes the row, back to the default.
              const dflt = rowMenu.isDefault ? rowMenu.row.permission : defaultFor(rowMenu.row)
              if (dflt) {
                return {
                  label: t('access.defaultOption', { level: PERMISSIONS.find(p => p.value === dflt)?.label ?? dflt }),
                  selected: !!rowMenu.isDefault,
                  onClick: () => {
                    const { row, isDefault } = rowMenu
                    setRowMenu(null)
                    if (!isDefault) void handleRevoke(row)
                  },
                }
              }
              const inh = rowMenu.inherited ? { permission: rowMenu.row.permission, uncertain: false } : (() => {
                const x = inheritedFor(rowMenu.row)
                return x ? { permission: x.row.permission, uncertain: x.uncertain } : null
              })()
              if (!inh) return undefined
              const level = PERMISSIONS.find(p => p.value === inh.permission)?.label ?? inh.permission
              return {
                // A closer level hidden from this viewer may decide: no level claimed.
                label: inh.uncertain ? t('access.inheritPlain') : t('access.inheritOption', { level }),
                selected: !!rowMenu.inherited,
                // On an explicit row: drop it, so the level from above applies again.
                onClick: () => {
                  const { row, inherited: fromAbove } = rowMenu
                  setRowMenu(null)
                  if (!fromAbove) void handleRevoke(row)
                },
              }
            })()}
            // Remove only where nothing is inherited — elsewhere "Inherit" is it.
            removeAction={rowMenu.inherited || rowMenu.isDefault || inheritedFor(rowMenu.row) || defaultFor(rowMenu.row) ? undefined : {
              label: `Remove ${labelForRow(rowMenu.row)}`,
              onClick: () => {
                const row = rowMenu.row
                setRowMenu(null)
                void handleRevoke(row)
              },
            }}
          />
        </div>,
        document.body,
      )}

      {/* Two-page "Add" popover: page 1 pick WHO, page 2 pick PERMISSION. */}
      {addOpen && addPos && createPortal(
        <div
          ref={addMenuRef}
          role="dialog"
          aria-label={addPage === 'who' ? t('access.addSomeone') : t('access.setPermission')}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          className={`kb-deck-portal ${themeClass} ws-menu fixed z-[320] w-[300px] flex flex-col`}
          style={{
            ...(addPos.flipped
              ? { bottom: window.innerHeight - addPos.anchorTop + 6 } // anchored by its bottom: grows upward
              : { top: addPos.top }),
            left: addPos.left,
            maxHeight: `min(60vh, 420px, ${addPos.room}px)`,
          }}
        >
          {addPage === 'who' ? (
            <>
              <div className="px-1.5 pt-1 pb-2">
                <div className="text-[10px] themed-text-muted uppercase tracking-wider px-1 mb-1.5">{t('access.add')}</div>
                <div className="flex gap-1" role="tablist">
                  {RECIPIENT_TABS.map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      role="tab"
                      aria-selected={tab === id}
                      data-active={tab === id || undefined}
                      data-testid={`share-tab-${id}`}
                      onClick={() => { setTab(id); setPickedRecipient(''); setRecipientQuery('') }}
                      className="kbg-pill flex-1 px-2 py-1.5 text-xs justify-center"
                    >
                      <Icon size={12} />
                      {label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  autoFocus
                  value={recipientQuery}
                  onChange={e => setRecipientQuery(e.target.value)}
                  placeholder={tab === 'email' ? t('access.searchMembers') : tab === 'team' ? t('access.filterTeams') : t('access.filterRoles')}
                  aria-label={tab === 'email' ? t('access.searchMembers') : tab === 'team' ? t('access.filterTeams') : t('access.filterRoles')}
                  data-testid="add-recipient-filter"
                  className="themed-input w-full mt-2 px-2.5 py-1.5 rounded-lg text-xs"
                />
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-1 pb-1.5">
                {renderRecipientList()}
              </div>
            </>
          ) : (() => {
            const rv = pickedRecipientView()
            return (
              <>
                {/* Header + choices scroll under the popover's capped height;
                    the Add button stays pinned below them. */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="flex items-center gap-2 px-1.5 pt-1.5 pb-2">
                  <button
                    type="button"
                    onClick={() => setAddPage('who')}
                    aria-label={t('access.back')}
                    className="w-6 h-6 rounded-md flex items-center justify-center themed-text-muted hover:themed-text hover:bg-white/10 cursor-pointer shrink-0"
                  >
                    <ArrowLeft size={14} />
                  </button>
                  {rv && (
                    <span className="flex items-center gap-2 min-w-0">
                      {rv.avatar}
                      <span className="min-w-0">
                        <span className="block text-sm themed-text truncate">{rv.name}</span>
                        <span className="block text-[11px] themed-text-muted truncate">{rv.sub}</span>
                      </span>
                    </span>
                  )}
                </div>
                <div className="px-1.5 pb-1" role="radiogroup" aria-label={t('access.permissionLevel')}>
                  {PERMISSIONS.map(p => {
                    const sel = pickedPermission === p.value
                    return (
                      <button
                        key={p.value}
                        type="button"
                        role="radio"
                        aria-checked={sel}
                        onClick={() => setPickedPermission(p.value)}
                        className="w-full flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors text-left cursor-pointer"
                      >
                        <Check size={13} className={`mt-0.5 shrink-0 ${sel ? 'text-blue-400 opacity-100' : 'opacity-0'}`} />
                        <span className="min-w-0">
                          <span className={`block text-sm ${sel ? 'text-blue-400 font-medium' : 'themed-text'}`}>{p.label}</span>
                          <span className="block text-[11px] themed-text-muted">{p.hint}</span>
                        </span>
                      </button>
                    )
                  })}
                </div>
                </div>
                <div className="shrink-0 px-1.5 pt-1 pb-1.5">
                  <button
                    onClick={handleAdd}
                    disabled={!pickedRecipient || busy}
                    data-testid="share-add"
                    className="w-full themed-btn-primary px-3 py-2 rounded-lg text-sm flex items-center justify-center gap-1.5 disabled:opacity-50 cursor-pointer"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    {t('access.addButton')}
                  </button>
                </div>
              </>
            )
          })()}
        </div>,
        document.body,
      )}
    </div>
  , document.body)
}
